/** "Notifications on this device" on the Me page: turn on/off, send a test, iPhone hint. */
import { useEffect, useState } from "react";
import { api } from "./api.ts";
import { currentPush, pushSupport, turnPushOff, turnPushOn } from "./push.ts";
import { Section } from "./ui.tsx";

export function NotificationsCard({ accountId, accountName, staff }: { accountId?: string; accountName?: string; staff: boolean }) {
  const support = pushSupport();
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    void currentPush().then((s) => setOn(!!s)).catch(() => setOn(false));
  }, []);

  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setMsg("");
    setError("");
    try {
      await f();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const blocked = support === "yes" && typeof Notification !== "undefined" && Notification.permission === "denied";
  return (
    <Section title="Notifications on this device">
      <div className="card pad" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {support === "ios-home-screen" && (
          <p style={{ margin: 0 }}>
            On iPhone, add Think-Control to your Home Screen first: tap <b>Share</b> then <b>Add to Home Screen</b>, open it from the new icon, and turn
            notifications on here.
          </p>
        )}
        {support === "no" && <p style={{ margin: 0 }}>This browser can't show notifications. Try Chrome, Edge, Firefox or Safari.</p>}
        {support === "yes" && (
          <>
            <p className="muted" style={{ margin: 0 }}>
              Serious alerts (offline, power lost, a safety rule switching something off, a gateway with no internet) show on this device as they happen
              {staff && accountName ? `, for ${accountName}` : ""}.
            </p>
            {blocked && <div className="error">Notifications are blocked for this site in the browser's settings. Allow them there, then try again.</div>}
            {staff && !accountId && <p style={{ margin: 0 }}>Open a customer first: staff get notifications for the farm they're looking at.</p>}
            <div className="toolbar">
              {on === false && (
                <button className="btn primary" disabled={busy || blocked || (staff && !accountId)} onClick={() => void run(async () => {
                  await turnPushOn(staff ? accountId : undefined);
                  setOn(true);
                  setMsg("Notifications are on for this device.");
                })}>
                  Turn notifications on
                </button>
              )}
              {on && (
                <>
                  <button className="btn" disabled={busy} onClick={() => void run(async () => {
                    const s = await currentPush();
                    const r = await api.pushTest(s?.endpoint);
                    setMsg(r.sent ? "Test sent: it should appear in a few seconds." : "The test didn't go through. Turn notifications off and on again.");
                  })}>
                    Send a test
                  </button>
                  <button className="btn ghost" disabled={busy} onClick={() => void run(async () => {
                    await turnPushOff();
                    setOn(false);
                    setMsg("Notifications are off for this device.");
                  })}>
                    Turn off
                  </button>
                </>
              )}
            </div>
          </>
        )}
        {msg && <p style={{ margin: 0 }}>{msg}</p>}
        {error && <div className="error">{error}</div>}
      </div>
    </Section>
  );
}
